'use client';

import { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import toast from 'react-hot-toast';
import { twMerge } from 'tailwind-merge';
import { tv } from 'tailwind-variants';
import { copyToClipboard, truncateText } from '@bridge/utils';

import { FlexContainer } from './FlexContainer';

import { faClone } from '@icons/classic/regular';

import type { SizeProp } from '@fortawesome/fontawesome-svg-core';
import type { VariantProps } from 'tailwind-variants';

const copyable = tv({
  base: 'cursor-pointer transition-colors self-end',
  variants: {
    variant: {
      light: 'text-content-secondary hover:text-content-primary',
      dark: 'text-content-primary hover:text-content-secondary',
    },
  },
  defaultVariants: {
    variant: 'light',
  },
});

export interface CopyableProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onCopy'>, VariantProps<typeof copyable> {
  className?: string;
  iconSize?: SizeProp;
  truncate?: boolean | number;
  truncateOptions?: {
    middle?: boolean;
  };
  rightAlign?: boolean;
  onCopy?: (value: string) => void;
  copyValue?: string;
  style?: React.CSSProperties;
  /**
   * Show a toast on copy: `true` for the default "Copied to clipboard" message, or a
   * string for a custom one. Off by default so the dashboard's toast wrapper (which
   * adds its own toast via `onCopy`) doesn't double up.
   */
  successToast?: string | boolean;
  /** Accessible label for the copy button. Give it context when a page has many copyables. */
  copyLabel?: string;
}

export default function Copyable({
  children,
  className,
  rightAlign,
  truncate,
  truncateOptions = { middle: true },
  variant,
  iconSize = 'sm',
  onCopy,
  copyValue,
  style,
  successToast,
  copyLabel = 'Copy',
}: CopyableProps) {
  const copyableRef = useRef<HTMLDivElement>(null);

  // Extract text content from children
  const originalText = typeof children === 'string' ? children : '';

  // Calculate truncated text if needed
  const displayText = (() => {
    if (!truncate || !originalText) return originalText;

    const truncateLength = typeof truncate === 'boolean' ? 50 : truncate;
    return truncateText(originalText, truncateLength, truncateOptions);
  })();

  const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const textToCopy = copyValue || originalText || copyableRef.current?.textContent || '';
    const success = await copyToClipboard(textToCopy);
    if (successToast) {
      if (success) {
        toast.success(typeof successToast === 'string' ? successToast : 'Copied to clipboard');
      } else {
        toast.error('Failed to copy');
      }
    }
    onCopy?.(textToCopy);
  };

  return (
    <FlexContainer
      className={twMerge('gap-1 inline-flex break-all whitespace-pre-line h-fit', rightAlign && 'justify-between')}
      items="center"
      style={style}
    >
      <div className={className} ref={copyableRef}>
        {truncate && typeof children === 'string' ? displayText : children}
      </div>
      <button className={copyable({ variant })} aria-label={copyLabel} onClick={handleCopy} type="button">
        <FontAwesomeIcon icon={faClone} size={iconSize} />
      </button>
    </FlexContainer>
  );
}
